import type {
  CampaignDocument,
  CampaignDocumentSource,
  CampaignRepository,
} from '../../domain/campaigns/repository';
import { parseCampaign } from '../../domain/campaigns/parse';
import type { Campaign } from '../../domain/campaigns/model';
import {
  firestoreClient,
  type FirestoreCollectionSource,
  type FirestoreDocumentValue,
} from './firestore';

function toDocumentSource(source: FirestoreCollectionSource): CampaignDocumentSource {
  return {
    subscribeCollection(onChange, onError) {
      return source.subscribeCollection('campaigns', 'name', onChange, onError);
    },
    subscribeDocument(campaignId, onChange, onError) {
      return source.subscribeDocument('campaigns', campaignId, onChange, onError);
    },
  };
}

function toCampaign(document: CampaignDocument): Campaign {
  return parseCampaign(document.id, document.data);
}

function toCampaignDocument(document: FirestoreDocumentValue): CampaignDocument {
  return document;
}

export function createCampaignRepository(
  source: CampaignDocumentSource = toDocumentSource(firestoreClient),
): CampaignRepository {
  return {
    subscribeAll(onChange, onError) {
      return source.subscribeCollection(
        (documents) => onChange(documents.filter((document) => document.exists).map(toCampaign)),
        onError,
      );
    },
    subscribeActive(onChange, onError) {
      return source.subscribeCollection(
        (documents) => onChange(
          documents
            .filter((document) => document.exists)
            .map(toCampaign)
            .filter((campaign) => !campaign.archivedAt),
        ),
        onError,
      );
    },
    subscribeOne(campaignId, onChange, onError) {
      if (!campaignId) {
        onChange(null);
        return () => undefined;
      }
      return source.subscribeDocument(
        campaignId,
        (document) => onChange(document.exists ? toCampaign(toCampaignDocument(document)) : null),
        onError,
      );
    },
  };
}

export const campaignRepository = createCampaignRepository();
